// The "Choose your Experience" screen, as the admin edits it.
//
// After a fan signs in, a club with several experiences (Firebirds: Acrisure
// Arena, CV Firebirds, CV Lakers) can show a full-screen page of big image
// tiles, one per experience, and the tile a fan taps becomes their experience.
// The admin configures the tiles, their order, one title and one background.
//
// It is ONE root node, tenant-wide — not under experiences/{id}, because the
// screen is what a fan sees BEFORE they are in any experience:
//
//   experienceChooser: {
//     title:           optional, <= 60 chars; the fan app says "Choose your
//                      Experience" when it is missing
//     backgroundImage: optional URL
//     buttons: { <experienceId>: { imageUrl?, order } }
//     updatedAt:       when the admin last saved
//   }
//
// A button is keyed by the experience's REAL key under `experiences` — the
// default experience's included ("cv-firebirds" on Firebirds, "default" on
// dev). Never by '', which is what experienceScope.js uses to MEAN the default
// on the admin's own screens: '' is not a key, and the rules refuse a button
// for anything that is not an experience.
//
// The fan app reads this in team_app_react_native/src/utils/
// experienceChooser.js; crossRepoExperienceChooser.test.js holds the two to
// the same field names and tile shape. The database rules are the third copy
// (database.rules.json, tested in functions/__tests__/databaseRules.emulator).
//
// Pure, so every decision here is testable without a browser: what gets
// saved, how reordering renumbers, and what the status line and warnings say.

import { isOnForEveryone, isOnFor, worksWithStatus } from './featureReachability';

export const CHOOSER_NODE = 'experienceChooser';
export const CHOOSER_FLAG = 'experienceChooser';
export const SWITCHER_FLAG = 'experienceSwitcher';

// Every value in this block has a twin exported by the fan app's
// src/utils/experienceChooser.js (named in the comment), and
// crossRepoExperienceChooser.test.js parses that file and fails when the two
// disagree. Change one side, change the other in the same breath.

export const DEFAULT_TITLE = 'Choose your Experience';   // DEFAULT_CHOOSER_TITLE
export const TITLE_MAX = 60;                               // CHOOSER_TITLE_MAX_LENGTH
export const URL_MAX = 2048;

// The node's fields, and a button's — the ONLY names either app uses. The
// rules file declares exactly these children; nodeFromDraft writes nothing
// else.
export const NODE_FIELDS = ['title', 'backgroundImage', 'buttons', 'updatedAt'];   // CHOOSER_NODE_FIELDS
export const BUTTON_FIELDS = ['imageUrl', 'order'];                                 // CHOOSER_BUTTON_FIELDS

// https only: iOS App Transport Security and Android's cleartext block both
// refuse http, so an http image is a tile every phone draws as its fallback.
// The rules refuse to store anything else (the same pattern, in
// database.rules.json). Case-sensitive here and in the rules, while the fan
// app's copy ignores case — so everything the admin writes, the fan draws.
export const IMAGE_URL_PATTERN = /^https:\/\//;   // CHOOSER_IMAGE_URL_PATTERN (fan: /i)

// Every tile is drawn at exactly 7:5, and the image IS the button — its
// colour included — shown uncropped. So the admin asks for 1400 × 1000.
export const TILE_ASPECT_RATIO = 1.4;   // CHOOSER_TILE_ASPECT_RATIO
export const TILE_ART = { width: 1400, height: 1000 };
// Drawn `cover`, so shorter phones lose the edges.
export const BACKGROUND_ART = { width: 1290, height: 2796 };

// How big a tile is drawn, in points (see tileWidthFor below).
export const TILE_WIDTH_FRACTION = 0.65;   // CHOOSER_TILE_WIDTH_FRACTION
export const TILE_MAX_WIDTH = 320;         // CHOOSER_TILE_MAX_WIDTH
export const TILE_MIN_WIDTH = 180;         // CHOOSER_TILE_MIN_WIDTH
export const TILE_GAP = 28;                // CHOOSER_TILE_GAP
export const FIT_TILES = 3;                // CHOOSER_FIT_TILES

// Fewer than this and the fan app skips the screen — one tile is not a choice.
export const MIN_BUTTONS = 2;   // CHOOSER_MIN_TILES

// How long the sign-in pause lasts, in the words both repos' docs and every
// admin warning use (contract §7). The fan app caps it at
// CHOOSER_WAIT_TIMEOUT_MS (3000).
export const SIGN_IN_PAUSE = 'usually under a second, at most 3 s';

// How long the editor waits on an image upload before giving up, so an
// upload that never answers cannot hold Save and the drop targets for ever.
export const UPLOAD_TIMEOUT_MS = 30000;

/**
 * Whether an image address is one Save may write: https (the rules refuse
 * anything else, and phones would not draw it) and not over the rules' cap.
 * What an upload must return before the editor will use it.
 */
export function isAcceptableImageUrl(url) {
    return typeof url === 'string' && url.length <= URL_MAX && IMAGE_URL_PATTERN.test(url);
}

/**
 * The tile width the fan app draws, in points — ChooseExperienceScreen's
 * sizing, step for step (contract §7):
 *
 *   width = max(MIN_WIDTH, min(windowWidth × WIDTH_FRACTION, MAX_WIDTH, fitHeightWidth))
 *   fitHeightWidth = ((availableHeight − titleBlockHeight − (k−1) × GAP) / k) × ASPECT_RATIO
 *   k = min(tileCount, FIT_TILES)
 *
 * So up to three tiles and the heading fit a phone without scrolling, however
 * short it is, until the tiles would drop under MIN_WIDTH; below that the
 * list scrolls instead. `availableHeight` is the window less its safe-area
 * insets and the screen's own vertical padding; left out, only the width
 * limits apply.
 */
export function tileWidthFor({ windowWidth, availableHeight, titleBlockHeight = 0, tileCount = 0 } = {}) {
    const width = Number.isFinite(windowWidth) && windowWidth > 0 ? windowWidth : 0;
    let size = Math.min(width * TILE_WIDTH_FRACTION, TILE_MAX_WIDTH);
    if (Number.isFinite(availableHeight) && availableHeight > 0) {
        const k = Math.max(1, Math.min(tileCount || 0, FIT_TILES));
        const fitHeightWidth = ((availableHeight - (titleBlockHeight || 0) - (k - 1) * TILE_GAP) / k) * TILE_ASPECT_RATIO;
        size = Math.min(size, fitHeightWidth);
    }
    // Down, not to nearest: a width rounded up by a fraction of a point is
    // what tips three tiles that exactly fit into a list that scrolls.
    return Math.floor(Math.max(TILE_MIN_WIDTH, size));
}

// The phones the preview can draw, in points, with the safe-area insets iOS
// gives them. The tall one is the phone the 1290 × 2796 background is made
// for (430 × 932 at @3x); the short one is an iPhone SE, the smallest screen
// the app still runs on, where fit-to-height actually bites.
export const PREVIEW_PHONES = [
    { id: 'tall', label: 'Tall phone', width: 430, height: 932, insetTop: 59, insetBottom: 34 },
    { id: 'short', label: 'Short phone', width: 375, height: 667, insetTop: 20, insetBottom: 0 }
];

// ChooseExperienceScreen's own layout, in points: the padding around the
// list, and the heading above it. Mirrored rather than pinned — they are not
// part of the shared contract — so check them against that screen's styles
// if the preview and a phone ever disagree.
export const SCREEN_LAYOUT = {
    paddingVertical: 32,       // CHOOSER_VERTICAL_PADDING (pinned when the fan exports it)
    paddingHorizontal: 16,     // CONTENT_PADDING_HORIZONTAL
    titleFontSize: 24,         // TITLE_FONT_SIZE
    titleLineHeight: 30,       // TITLE_FONT_SIZE × LINE_HEIGHT_EM (1.25), the app's own first guess
    titleMarginBottom: 12,     // TITLE_MARGIN_BOTTOM
    titleMaxLines: 2,          // the heading's numberOfLines (the app shrinks the font to fit two)
    // The fallback tile's name, drawn when a button has no image.
    tileNameFontSize: 22,
    tileNamePadding: 16
};

// The heading wraps; the preview cannot measure the app's font, so it counts
// lines the way a greedy wrap would with an average bold glyph this wide (in
// ems). Close enough to know whether a long title costs the tiles a line.
// Never more than titleMaxLines: past two, the app shrinks the font instead.
const TITLE_GLYPH_EM = 0.58;

function titleLines(title, lineWidth) {
    const perLine = Math.max(1, Math.floor(lineWidth / (SCREEN_LAYOUT.titleFontSize * TITLE_GLYPH_EM)));
    let lines = 1;
    let used = 0;
    String(title || '').trim().split(/\s+/).filter(Boolean).forEach(word => {
        const length = word.length;
        if (used === 0) {
            lines += Math.floor((length - 1) / perLine);
            used = ((length - 1) % perLine) + 1;
        } else if (used + 1 + length <= perLine) {
            used += 1 + length;
        } else {
            lines += 1 + Math.floor((length - 1) / perLine);
            used = ((length - 1) % perLine) + 1;
        }
    });
    return Math.min(lines, SCREEN_LAYOUT.titleMaxLines);
}

/**
 * How the fan app lays the screen out on one of PREVIEW_PHONES, in points:
 * the tile width tileWidthFor gives it, and whether the list then scrolls.
 * The preview draws exactly these numbers (as CSS custom properties), so
 * nothing about its sizing lives in the stylesheet.
 */
export function previewLayout(phone, { title, tileCount = 0 } = {}) {
    const p = phone || PREVIEW_PHONES[0];
    const L = SCREEN_LAYOUT;
    const availableHeight = p.height - p.insetTop - p.insetBottom - 2 * L.paddingVertical;
    const lines = titleLines(title || DEFAULT_TITLE, p.width - 2 * L.paddingHorizontal);
    // The heading, its margin, and the list gap that follows it.
    const titleBlockHeight = lines * L.titleLineHeight + L.titleMarginBottom + TILE_GAP;
    const tileWidth = tileWidthFor({ windowWidth: p.width, availableHeight, titleBlockHeight, tileCount });
    const count = Math.max(0, tileCount || 0);
    const contentHeight = titleBlockHeight - (count > 0 ? 0 : TILE_GAP)
        + count * (tileWidth / TILE_ASPECT_RATIO) + Math.max(0, count - 1) * TILE_GAP;
    return {
        phone: p,
        tileWidth,
        titleLines: lines,
        availableHeight,
        titleBlockHeight,
        scrolls: contentHeight > availableHeight + 1
    };
}

// The same visibility test the fan app applies (experienceResolution.js
// isVisible): a missing `live` is visible, only an explicit false is a draft.
export const isLiveExperience = (exp) => !!exp && exp.live !== false;

const nameOf = (id, experiences) => {
    const exp = (experiences || {})[id];
    return (exp && typeof exp.name === 'string' && exp.name.trim()) || id;
};

// A usable order, or null. Only a finite number counts — the fan app's test.
const orderOf = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

export const emptyDraft = () => ({ title: '', backgroundImage: '', buttons: [] });

/**
 * The order the fan app draws tiles in — its compareTiles, step for step:
 * `order` (a missing one last), then lower-cased name, then id, compared by
 * code unit rather than by locale. The admin always saves 1..n so ties never
 * arise from it; this matters for data written by hand or before the rules,
 * which the screen must show in the order fans actually see it.
 */
export function sortButtons(buttons, experiences) {
    const compare = (x, y) => (x === y ? 0 : (x < y ? -1 : 1));
    return [...(buttons || [])].sort((a, b) => {
        const aOrder = orderOf(a.order);
        const bOrder = orderOf(b.order);
        if (aOrder !== bOrder) {
            if (aOrder === null) return 1;
            if (bOrder === null) return -1;
            return aOrder - bOrder;
        }
        return compare(String(nameOf(a.id, experiences)).toLowerCase(), String(nameOf(b.id, experiences)).toLowerCase())
            || compare(String(a.id), String(b.id));
    });
}

// 1..n in list order. Orders only ever mean "position", so the admin rewrites
// them rather than preserving gaps a delete left behind.
export function renumber(buttons) {
    return (buttons || []).map((button, i) => ({ ...button, order: i + 1 }));
}

/**
 * The stored node, as the list the screen edits.
 *
 * A button whose experience no longer exists is dropped and reported: the fan
 * app ignores it already, and the rules would refuse to save it back, so
 * keeping it would make every later save fail. It only arises from an
 * experience deleted by an admin build older than this screen.
 */
export function draftFromNode(node, experiences) {
    const value = node && typeof node === 'object' ? node : {};
    const known = experiences || {};
    const stored = value.buttons && typeof value.buttons === 'object' ? value.buttons : {};
    const orphaned = [];
    const buttons = [];
    Object.entries(stored).forEach(([id, button]) => {
        if (!id || !known[id]) {
            orphaned.push(id);
            return;
        }
        buttons.push({
            id,
            imageUrl: button && typeof button.imageUrl === 'string' ? button.imageUrl : '',
            order: orderOf(button && button.order)
        });
    });
    return {
        draft: {
            title: typeof value.title === 'string' ? value.title : '',
            backgroundImage: typeof value.backgroundImage === 'string' ? value.backgroundImage : '',
            buttons: renumber(sortButtons(buttons, known))
        },
        orphaned: orphaned.sort()
    };
}

/**
 * EXACTLY what Save writes, with one set() of the whole node.
 *
 * A blank title or background is left out rather than written empty — the fan
 * app's defaults apply to a missing field, and an absent key is the one shape
 * every reader agrees on. `undefined` never appears: Firebase refuses a write
 * carrying one outright.
 */
export function nodeFromDraft(draft, now) {
    const source = draft || emptyDraft();
    const out = {};
    const title = String(source.title || '').trim();
    if (title) out.title = title;
    const backgroundImage = String(source.backgroundImage || '').trim();
    if (backgroundImage) out.backgroundImage = backgroundImage;
    const buttons = {};
    // Defensive: '' is the admin's name for the default experience on other
    // screens and must never reach the database as a key.
    renumber((source.buttons || []).filter(button => button && button.id)).forEach(button => {
        const imageUrl = String(button.imageUrl || '').trim();
        buttons[button.id] = imageUrl
            ? { imageUrl, order: button.order }
            : { order: button.order };
    });
    if (Object.keys(buttons).length > 0) out.buttons = buttons;
    out.updatedAt = now;
    return out;
}

// When the stored screen was last saved (its `updatedAt`), or null for a
// screen that does not exist or was never stamped.
export function stampOf(node) {
    return node && typeof node === 'object' && typeof node.updatedAt === 'number' && Number.isFinite(node.updatedAt)
        ? node.updatedAt
        : null;
}

/**
 * Has someone else saved the screen since this editor read it?
 *
 * Save writes the whole node, so a save from a page opened before another
 * admin's would silently put their changes back the way this page found
 * them. Every save stamps `updatedAt`, so the stamp this page read is the
 * version it is editing: a different stamp now — or a screen that has since
 * appeared where there was none, or vanished where there was one — means the
 * draft is based on something no longer there.
 *
 * @param current      the node as it is now (null when absent)
 * @param loadedStamp  stampOf(the node this editor read), null when there was none
 */
export function savedSince(current, loadedStamp) {
    if (current === null || current === undefined) return loadedStamp !== null;
    return stampOf(current) !== loadedStamp;
}

// Would Save change anything? Compared on what would be WRITTEN, so a title
// that differs only by trailing space, or a reorder that lands back where it
// started, is not an unsaved change.
export function isDirty(draft, baseline) {
    return JSON.stringify(nodeFromDraft(draft, 0)) !== JSON.stringify(nodeFromDraft(baseline, 0));
}

// Swap with a neighbour, then renumber — the Bottom Tabs algorithm.
export function moveButton(buttons, index, direction) {
    const list = [...(buttons || [])];
    const target = index + direction;
    if (index < 0 || index >= list.length || target < 0 || target >= list.length) return buttons;
    [list[index], list[target]] = [list[target], list[index]];
    return renumber(list);
}

// A new button goes last, with no image yet.
export function addButton(buttons, experienceId) {
    const list = buttons || [];
    if (!experienceId || list.some(button => button.id === experienceId)) return list;
    return renumber([...list, { id: experienceId, imageUrl: '', order: list.length + 1 }]);
}

export function withoutButton(buttons, experienceId) {
    return renumber((buttons || []).filter(button => button.id !== experienceId));
}

export function withButtonImage(buttons, experienceId, imageUrl) {
    return (buttons || []).map(button =>
        (button.id === experienceId ? { ...button, imageUrl: imageUrl || '' } : button));
}

/**
 * The experiences that do not have a button yet, for the "Add a button" list,
 * in the Experiences list's own order. Keys are the REAL experience keys.
 */
export function addableExperiences(experiences, buttons) {
    const taken = new Set((buttons || []).map(button => button.id));
    return Object.entries(experiences || {})
        .filter(([id, exp]) => id && exp && !taken.has(id))
        .map(([id, exp]) => ({
            id,
            name: exp.name || id,
            draft: exp.live === false,
            order: Number(exp.order) || 0,
            isDefault: exp.isDefault === true
        }))
        .sort((a, b) => (a.order - b.order)
            || (b.isDefault - a.isDefault)
            || a.name.localeCompare(b.name))
        .map(option => ({
            id: option.id,
            draft: option.draft,
            label: `${option.name} (${option.id})${option.draft ? ' — draft' : ''}`
        }));
}

/**
 * Something Save must refuse before it reaches the rules, as a sentence, or
 * null. The inputs cap these already; this is for data that arrived some
 * other way.
 */
export function saveProblem(draft, experiences) {
    const source = draft || emptyDraft();
    if (String(source.title || '').trim().length > TITLE_MAX) {
        return `Keep the title to ${TITLE_MAX} characters or fewer.`;
    }
    const urls = [source.backgroundImage, ...(source.buttons || []).map(button => button.imageUrl)];
    if (urls.some(url => String(url || '').length > URL_MAX)) {
        return 'One of the images has an address too long to save. Upload it again.';
    }
    // Only a stored address written before the rules refused them can get
    // here — uploads are checked as they land. Named, because the operator has
    // to go and replace that one image.
    const insecure = (url) => !!String(url || '').trim() && !IMAGE_URL_PATTERN.test(String(url).trim());
    if (insecure(source.backgroundImage)) {
        return 'The background image is not an https address, so phones would not show it and the database refuses it. Upload it again, or remove it.';
    }
    const button = (source.buttons || []).find(b => insecure(b.imageUrl));
    if (button) {
        return `The image on "${nameOf(button.id, experiences)}" is not an https address, so phones would not show it and the database refuses it. Upload it again.`;
    }
    return null;
}

/**
 * Who actually gets the screen, as the fan app decides it.
 *
 * Per person, the chooser must resolve on AND Change Experience must resolve
 * on — the app gates the one on the other for each account. So "switched on
 * for everyone" with the menu hidden is nobody, and a tester whose menu is on
 * gets it while everyone else does not. That count is worksWithStatus, which
 * Feature Access's row caption reads too, so the two screens cannot disagree.
 *
 * And nobody at all when fans cannot sign in (root settings.hideLoginFlow):
 * the screen follows sign-in, and that app never shows one. Feature Access
 * will not offer the switch there, but a switch set before the login flow was
 * hidden still reads as on, and this line must not say "On for everyone" for
 * a screen no fan can reach. `loginActive` unknown (undefined) counts as yes,
 * the same bargain loginIsActive makes.
 *
 * @returns {
 *   state:   'off' | 'testers' | 'everyone',
 *   label:   what the status line says,
 *   testers: how many testers get it (state 'testers'),
 *   except:  how many are set to not get it (state 'everyone'),
 *   switcherGap: null, or { everyone: true } when the chooser is on for
 *            everyone but the menu is not, or { testers: n } when n testers
 *            have the chooser without the menu
 * }
 */
export function chooserStatus({ flags = {}, access = {}, loginActive } = {}) {
    if (loginActive === false) {
        return { state: 'off', label: 'Off', testers: 0, except: 0, switcherGap: null, noLogin: true };
    }
    const safeFlags = flags || {};
    const entries = Object.values(access || {}).filter(entry => entry && typeof entry === 'object');
    const chooserEveryone = isOnForEveryone(CHOOSER_FLAG, safeFlags);
    const switcherEveryone = isOnForEveryone(SWITCHER_FLAG, safeFlags);

    let switcherGap = null;
    if (chooserEveryone && !switcherEveryone) {
        switcherGap = { everyone: true };
    } else if (!switcherEveryone) {
        const blocked = entries.filter(entry =>
            isOnFor(entry, CHOOSER_FLAG, safeFlags) && !isOnFor(entry, SWITCHER_FLAG, safeFlags)).length;
        if (blocked > 0) switcherGap = { testers: blocked };
    }

    const who = worksWithStatus(CHOOSER_FLAG, SWITCHER_FLAG, { flags: safeFlags, access });
    const label = { everyone: 'On for everyone', testers: 'On for testers', off: 'Off' }[who.state];
    return { state: who.state, label, testers: who.testers, except: who.except, switcherGap };
}

/**
 * What would stop fans seeing the screen as set up, one short sentence each.
 * Shown whatever the switch says: the point is to be ready BEFORE it goes on.
 */
export function chooserWarnings({ status, buttons, experiences } = {}) {
    const list = buttons || [];
    const known = experiences || {};
    const warnings = [];

    if (status && status.noLogin) {
        warnings.push({
            id: 'noLogin',
            text: 'Fans of this app cannot sign in (Hide Login Flow is on in Settings), so none of them get this screen.'
        });
    }

    if (status && status.switcherGap) {
        const n = status.switcherGap.testers;
        warnings.push({
            id: 'switcher',
            text: status.switcherGap.everyone
                ? 'Change Experience is not on for everyone, and fans without it never get this screen.'
                : `${n} ${n === 1 ? 'tester has' : 'testers have'} this screen switched on without Change Experience, so they do not get it.`
        });
    }

    const live = list.filter(button => isLiveExperience(known[button.id]));
    if (live.length < MIN_BUTTONS) {
        warnings.push({
            id: 'tooFew',
            text: live.length === 0
                ? 'Fans need at least two live buttons to choose between. Until there are two, the app skips this screen.'
                : 'Only one button leads to a live experience. Fans need two to choose between, so until then the app skips this screen.'
        });
    }

    // At the moment a fan signs in, the app cannot know whether that fan is a
    // tester until it has read their own switches, so it holds them on a
    // loading screen while it looks. It only looks when the screen could
    // actually show (two live buttons), which makes this a cost of exactly one
    // state: buttons ready, screen not on for everyone. Everyone signing in
    // pays it, testers or not, for as long as that state lasts.
    if (status && !status.noLogin && status.state !== 'everyone' && live.length >= MIN_BUTTONS) {
        warnings.push({
            id: 'signInWait',
            text: `While buttons are ready but this screen is not on for everyone, the app pauses on a loading screen for a moment after every sign-in (${SIGN_IN_PAUSE}) to check whether that fan is a tester. The pause goes on for as long as the buttons stay set up and the screen is not on for everyone — switched off included — so set the buttons up shortly before you switch it on, and if you switch it off for good, remove the buttons too.`
        });
    }

    list.filter(button => known[button.id] && known[button.id].live === false).forEach(button => {
        warnings.push({
            id: `draft:${button.id}`,
            text: `"${nameOf(button.id, known)}" is a draft, so its button stays hidden from fans until you publish it.`
        });
    });

    list.filter(button => !button.imageUrl && isLiveExperience(known[button.id])).forEach(button => {
        warnings.push({
            id: `image:${button.id}`,
            text: `"${nameOf(button.id, known)}" has no image yet, so fans get a plain tile with its name.`
        });
    });

    return warnings;
}

/**
 * The experience a fan is in until they choose, or when the screen is not
 * shown to them — the fan app's fallback: a live primary, else the live
 * default. Null when neither resolves (the app then runs on its root config).
 */
export function landingExperience(experiences) {
    const entries = Object.entries(experiences || {})
        .filter(([, exp]) => isLiveExperience(exp))
        .map(([id, exp]) => ({ id, ...exp }));
    const primary = entries.find(exp => exp.isPrimary);
    if (primary) return { id: primary.id, name: primary.name || primary.id, isPrimary: true };
    const fallback = entries.find(exp => exp.isDefault);
    return fallback ? { id: fallback.id, name: fallback.name || fallback.id, isPrimary: false } : null;
}
